// @flow
import update from 'immutability-helper';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import {postForecastDiyPriceApi, postForecastClassicPriceApi} from '../new-order/QuoteStep/api';
import {
  deleteShoppingCartApi,
  deleteShoppingCartItemApi,
  endpoints,
  getShoppingCartApi,
  postItemsAutoShippingApi,
  postShoppingCartItemApi,
  putShoppingCartItemApi,
  deleteShoppingCartItemsApi,
} from './api';
import { uploadImageApi, uploadImageURLApi } from '../new-order/api';
import { dataURItoBlob } from '../../utils/file';
import { setNewAlert } from '../alert/duck';
import { push } from 'react-router-redux';
import {
  getVirtualCart, removeVirtualCart, setVirtualCart,
} from './utils/virtual-cart-storage';
import analytics from '../../analytics';

import type { HTTPError } from '../../error';
import type {ForecastCourier, ForecastPricesApiReq, ForecastTier} from '../new-order/QuoteStep/api';
import type {
  ShippingRequestApi, ShippingResponse,
  ShoppingCartItemRequestApi,
  ShoppingCartRequestApi,
  VirtualItem,
  VirtualItemRequestApi
} from './api';
import type { ShoppingCartItem } from '../../entities';
import type { RootState } from '../index';

export type ShoppingCartAction = {
  +type: string;
  +amount?: number;
  +shipping?: string;
  +error?: HTTPError;
  +imageId?: string;
  +imageIds?: string[];
  +itemNumber?: number;
  +shoppingCartRequest?: ShoppingCartRequestApi;
  +shoppingCart?: ShoppingCartItem[];
  +virtualShoppingCartRequest?: VirtualItemRequestApi;
  +virtualShoppingCart?: VirtualItem[];
  +index?: number;
  +forecastClassic?: ForecastTier[];
  +forecastCouriers?: ForecastCourier[];
  +forecastRequest?: ForecastPricesApiReq;
  +shoppingCartItemIds?: string[];
};

export type ShoppingCartState = {
  +isFetchingCart?: boolean;
  // writable
  shoppingCart?: ShoppingCartItem[];
  +virtualShoppingCart: VirtualItem[];
  +virtualShoppingCartInitialized?: boolean;
  +fetchCartError?: HTTPError;

  +isDeletingCart?: boolean;
  +deleteCartError?: HTTPError;

  +isPostingCartItem?: boolean;
  +postCartItemSuccess?: boolean;
  +postCartItemError?: HTTPError;

  +isEditingItem?: boolean;
  +editItemError?: HTTPError;

  +isDeletingItem?: boolean;
  +deleteItemError?: HTTPError;

  +isPostingToCart?: boolean;
  +postToCartError?: HTTPError;
};

const SHOPPING_CART_MAX_QUANTITY = 5;

// Actions
const VIRTUAL_SHOPPING_CART_FETCH_CART_REQUEST = 'VIRTUAL_SHOPPING_CART_FETCH_CART_REQUEST';
const VIRTUAL_SHOPPING_CART_DELETE_CART_REQUEST = 'VIRTUAL_SHOPPING_CART_DELETE_CART_REQUEST';

const VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST = 'VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST';
const VIRTUAL_SHOPPING_CART_POST_ITEM_SUCCESS = 'VIRTUAL_SHOPPING_CART_POST_ITEM_SUCCESS';
const VIRTUAL_SHOPPING_CART_POST_ITEM_ERROR = 'VIRTUAL_SHOPPING_CART_POST_ITEM_ERROR';

const VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST = 'VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST';
const VIRTUAL_SHOPPING_CART_EDIT_ITEM_SUCCESS = 'VIRTUAL_SHOPPING_CART_EDIT_ITEM_SUCCESS';
const VIRTUAL_SHOPPING_CART_EDIT_ITEM_ERROR = 'VIRTUAL_SHOPPING_CART_EDIT_ITEM_ERROR';

const VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST = 'VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST';
const VIRTUAL_SHOPPING_CART_DELETE_ITEM_SUCCESS = 'VIRTUAL_SHOPPING_CART_DELETE_ITEM_SUCCESS';
const VIRTUAL_SHOPPING_CART_DELETE_ITEM_ERROR = 'VIRTUAL_SHOPPING_CART_DELETE_ITEM_ERROR';

const VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST = 'VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST';
const VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_DELETE_SUCCESS = 'VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_DELETE_SUCCESS';
const VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_IMAGE_SUCCESS = 'VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_IMAGE_SUCCESS';
const VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_SUCCESS = 'VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_SUCCESS';
const VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_ERROR = 'VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_ERROR';

const SHOPPING_CART_FETCH_CART_REQUEST = 'SHOPPING_CART_FETCH_CART_REQUEST';
const SHOPPING_CART_FETCH_CART_SUCCESS = 'SHOPPING_CART_FETCH_CART_SUCCESS';
const SHOPPING_CART_FETCH_CART_ERROR = 'SHOPPING_CART_FETCH_CART_ERROR';

const SHOPPING_CART_DELETE_CART_ITEMS_REQUEST = 'SHOPPING_CART_DELETE_CART_ITEMS_REQUEST';
const SHOPPING_CART_DELETE_CART_ITEMS_SUCCESS = 'SHOPPING_CART_DELETE_CART_ITEMS_SUCCESS';
const SHOPPING_CART_DELETE_CART_ITEMS_ERROR = 'SHOPPING_CART_DELETE_CART_ITEMS_ERROR';

const SHOPPING_CART_DELETE_CART_REQUEST = 'SHOPPING_CART_DELETE_CART_REQUEST';
const SHOPPING_CART_DELETE_CART_SUCCESS = 'SHOPPING_CART_DELETE_CART_SUCCESS';
const SHOPPING_CART_DELETE_CART_ERROR = 'SHOPPING_CART_DELETE_CART_ERROR';

const SHOPPING_CART_POST_ITEM_REQUEST = 'SHOPPING_CART_POST_ITEM_REQUEST';
const SHOPPING_CART_POST_ITEM_IMAGE_SUCCESS = 'SHOPPING_CART_POST_ITEM_IMAGE_SUCCESS';
const SHOPPING_CART_POST_ITEM_SUCCESS = 'SHOPPING_CART_POST_ITEM_SUCCESS';
const SHOPPING_CART_POST_ITEM_ERROR = 'SHOPPING_CART_POST_ITEM_ERROR';

const SHOPPING_CART_EDIT_ITEM_REQUEST = 'SHOPPING_CART_EDIT_ITEM_REQUEST';
const SHOPPING_CART_EDIT_ITEM_SUCCESS = 'SHOPPING_CART_EDIT_ITEM_SUCCESS';
const SHOPPING_CART_EDIT_ITEM_ERROR = 'SHOPPING_CART_EDIT_ITEM_ERROR';

const SHOPPING_CART_DELETE_ITEM_REQUEST = 'SHOPPING_CART_DELETE_ITEM_REQUEST';
const SHOPPING_CART_DELETE_ITEM_SUCCESS = 'SHOPPING_CART_DELETE_ITEM_SUCCESS';
const SHOPPING_CART_DELETE_ITEM_ERROR = 'SHOPPING_CART_DELETE_ITEM_ERROR';

const SHOPPING_CART_FORECAST_ITEM_REQUEST = 'SHOPPING_CART_FORECAST_ITEM_REQUEST';
const SHOPPING_CART_FORECAST_ITEM_SUCCESS = 'SHOPPING_CART_FORECAST_ITEM_SUCCESS';
const SHOPPING_CART_FORECAST_ITEM_ERROR = 'SHOPPING_CART_FORECAST_ITEM_ERROR';

//Action creators
export function fetchVirtualShoppingCart(): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_FETCH_CART_REQUEST,
  };
}

export function deleteVirtualShoppingCart(): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_DELETE_CART_REQUEST,
  };
}

export function postVirtualShoppingCartItem(virtualShoppingCartRequest: VirtualItemRequestApi): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST,
    virtualShoppingCartRequest,
  };
}

export function postVirtualShoppingCartItemSuccess(virtualShoppingCart: VirtualItem[]): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_ITEM_SUCCESS,
    virtualShoppingCart,
  };
}

export function postVirtualShoppingCartItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_ITEM_ERROR,
    error,
  };
}

export function editVirtualShoppingCartItem(index: number, amount?: number, shipping?: string): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST,
    index,
    amount,
    shipping,
  };
}

export function editVirtualShoppingCartItemSuccess(virtualShoppingCart: VirtualItem[]): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_EDIT_ITEM_SUCCESS,
    virtualShoppingCart,
  };
}

export function editVirtualShoppingCartItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_EDIT_ITEM_ERROR,
    error,
  };
}

export function deleteVirtualCartItem(index: number): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST,
    index,
  };
}

export function deleteVirtualCartItemSuccess(virtualShoppingCart: VirtualItem[]): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_DELETE_ITEM_SUCCESS,
    virtualShoppingCart,
  };
}

export function deleteVirtualCartItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_DELETE_ITEM_ERROR,
    error,
  };
}

export function postVirtualCartToCart(shoppingCartRequest: ShoppingCartRequestApi): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST,
    shoppingCartRequest,
  };
}

export function postVirtualCartToCartDeleteSuccess(shoppingCartRequest: ShoppingCartRequestApi): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_DELETE_SUCCESS,
    shoppingCartRequest,
  };
}

export function postVirtualCartToCartImageSuccess(imageIds: string[], shoppingCartRequest: ShoppingCartRequestApi): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_IMAGE_SUCCESS,
    imageIds,
    shoppingCartRequest,
  };
}

export function postVirtualCartToCartSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_SUCCESS,
    shoppingCart,
  };
}

export function postVirtualCartToCartErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_ERROR,
    error,
  };
}

export function fetchShoppingCart(): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FETCH_CART_REQUEST,
  };
}

export function fetchShoppingCartSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FETCH_CART_SUCCESS,
    shoppingCart,
  };
}

export function fetchShoppingCartErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FETCH_CART_ERROR,
    error,
  };
}


export function deleteShoppingCartItems(shoppingCartItemIds: string[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_ITEMS_REQUEST,
    shoppingCartItemIds,
  };
}

export function deleteShoppingCartItemsSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_ITEMS_SUCCESS,
    shoppingCart,
  };
}

export function deleteShoppingCartItemsErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_ITEMS_ERROR,
    error,
  };
}

export function deleteShoppingCart(): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_REQUEST,
  };
}

export function deleteShoppingCartSuccess(): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_SUCCESS,
  };
}

export function deleteShoppingCartErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_CART_ERROR,
    error,
  };
}

export function postShoppingCartItem(shoppingCartRequest: ShoppingCartRequestApi): ShoppingCartAction {
  return {
    type: SHOPPING_CART_POST_ITEM_REQUEST,
    shoppingCartRequest,
  };
}

export function postShoppingCartItemImageSuccess(imageId: string, shoppingCartRequest: ShoppingCartRequestApi): ShoppingCartAction {
  return {
    type: SHOPPING_CART_POST_ITEM_IMAGE_SUCCESS,
    imageId,
    shoppingCartRequest,
  }
}

export function postShoppingCartItemSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_POST_ITEM_SUCCESS,
    shoppingCart,
  };
}

export function postShoppingCartItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_POST_ITEM_ERROR,
    error,
  };
}

export function editShoppingCartItem(itemNumber: number, amount?: number, shipping?: string): ShoppingCartAction {
  return {
    type: SHOPPING_CART_EDIT_ITEM_REQUEST,
    itemNumber,
    amount,
    shipping,
  };
}

export function editShoppingCartItemSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_EDIT_ITEM_SUCCESS,
    shoppingCart,
  };
}

export function editShoppingCartItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_EDIT_ITEM_ERROR,
    error,
  };
}

export function deleteItem(itemNumber: number): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_ITEM_REQUEST,
    itemNumber,
  };
}

export function deleteItemSuccess(shoppingCart: ShoppingCartItem[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_ITEM_SUCCESS,
    shoppingCart,
  };
}

export function deleteItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: SHOPPING_CART_DELETE_ITEM_ERROR,
    error,
  };
}

export function forecastShoppingCartItem(itemNumber: number): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FORECAST_ITEM_REQUEST,
    itemNumber,
  };
}
export function forecastShoppingCartItemSuccess(itemNumber: number, 
                                                forecastClassic?: ForecastTier[], 
                                                forecastCouriers?: ForecastCourier[]): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FORECAST_ITEM_SUCCESS,
    itemNumber,
    forecastClassic,
    forecastCouriers,
  };
}

export function forecastShoppingCartItemError(itemNumber: number): ShoppingCartAction {
  return {
    type: SHOPPING_CART_FORECAST_ITEM_ERROR,
    itemNumber,
  };
}

export const actions = {
  VIRTUAL_SHOPPING_CART_FETCH_CART_REQUEST,
  VIRTUAL_SHOPPING_CART_DELETE_CART_REQUEST,
  VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST,
  VIRTUAL_SHOPPING_CART_POST_ITEM_SUCCESS,
  VIRTUAL_SHOPPING_CART_POST_ITEM_ERROR,
  VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST,
  VIRTUAL_SHOPPING_CART_EDIT_ITEM_SUCCESS,
  VIRTUAL_SHOPPING_CART_EDIT_ITEM_ERROR,
  VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST,
  VIRTUAL_SHOPPING_CART_DELETE_ITEM_SUCCESS,
  VIRTUAL_SHOPPING_CART_DELETE_ITEM_ERROR,
  VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST,
  VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_DELETE_SUCCESS,
  VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_IMAGE_SUCCESS,
  VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_SUCCESS,
  VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_ERROR,

  SHOPPING_CART_FETCH_CART_REQUEST,
  SHOPPING_CART_FETCH_CART_SUCCESS,
  SHOPPING_CART_FETCH_CART_ERROR,

  SHOPPING_CART_DELETE_CART_ITEMS_REQUEST,
  SHOPPING_CART_DELETE_CART_ITEMS_SUCCESS,
  SHOPPING_CART_DELETE_CART_ITEMS_ERROR,
  
  SHOPPING_CART_DELETE_CART_REQUEST,
  SHOPPING_CART_DELETE_CART_SUCCESS,
  SHOPPING_CART_DELETE_CART_ERROR,
  SHOPPING_CART_POST_ITEM_REQUEST,
  SHOPPING_CART_POST_ITEM_SUCCESS,
  SHOPPING_CART_POST_ITEM_ERROR,
  SHOPPING_CART_EDIT_ITEM_REQUEST,
  SHOPPING_CART_EDIT_ITEM_SUCCESS,
  SHOPPING_CART_EDIT_ITEM_ERROR,
  SHOPPING_CART_DELETE_ITEM_REQUEST,
  SHOPPING_CART_DELETE_ITEM_SUCCESS,
  SHOPPING_CART_DELETE_ITEM_ERROR,
  SHOPPING_CART_FORECAST_ITEM_REQUEST,
  SHOPPING_CART_FORECAST_ITEM_SUCCESS,
  SHOPPING_CART_FORECAST_ITEM_ERROR,
  
  fetchVirtualShoppingCart,
  deleteVirtualShoppingCart,
  postVirtualShoppingCartItem,
  postVirtualShoppingCartItemSuccess,
  postVirtualShoppingCartItemErrorFn,
  editVirtualShoppingCartItem,
  editVirtualShoppingCartItemSuccess,
  editVirtualShoppingCartItemErrorFn,
  deleteVirtualCartItem,
  deleteVirtualCartItemSuccess,
  deleteVirtualCartItemErrorFn,
  postVirtualCartToCart,
  postVirtualCartToCartImageSuccess,
  postVirtualCartToCartSuccess,
  postVirtualCartToCartErrorFn,

  fetchShoppingCart,
  fetchShoppingCartSuccess,
  fetchShoppingCartErrorFn,

  deleteShoppingCartItems,
  deleteShoppingCartItemsSuccess,
  deleteShoppingCartItemsErrorFn,
  
  deleteShoppingCart,
  deleteShoppingCartSuccess,
  deleteShoppingCartErrorFn,
  postShoppingCartItem,
  postVirtualCartToCartDeleteSuccess,
  postShoppingCartItemImageSuccess,
  postShoppingCartItemSuccess,
  postShoppingCartItemErrorFn,
  editShoppingCartItem,
  editShoppingCartItemSuccess,
  editShoppingCartItemErrorFn,
  deleteItem,
  deleteItemSuccess,
  deleteItemErrorFn,
  forecastShoppingCartItem,
  forecastShoppingCartItemSuccess,
  forecastShoppingCartItemError,
};

export const initialState: ShoppingCartState = {
  virtualShoppingCart: [],
};

// Reducer
export default function reducer(state: ShoppingCartState = initialState, action: ShoppingCartAction) {
  switch (action.type) {
    case VIRTUAL_SHOPPING_CART_FETCH_CART_REQUEST:
      const virtualCart = getVirtualCart() ? getVirtualCart().slice(0, SHOPPING_CART_MAX_QUANTITY) : [];
      return update(state, {
        virtualShoppingCart: { $set: virtualCart },
        virtualShoppingCartInitialized: { $set: true },
      });
    case VIRTUAL_SHOPPING_CART_DELETE_CART_REQUEST:
      removeVirtualCart();
      return update(state, {
        virtualShoppingCart: {
          $set: [],
        }
      });
    case VIRTUAL_SHOPPING_CART_POST_ITEM_SUCCESS:
      if (action.virtualShoppingCart) setVirtualCart(action.virtualShoppingCart);
      return update(state, {
        virtualShoppingCart: { $set: action.virtualShoppingCart },
        postCartItemSuccess: { $set: true },
        $unset: ['isPostingCartItem'],
      });
    case VIRTUAL_SHOPPING_CART_EDIT_ITEM_SUCCESS:
      if (action.virtualShoppingCart) setVirtualCart(action.virtualShoppingCart);
      return update(state, {
        virtualShoppingCart: { $set: action.virtualShoppingCart },
        $unset: ['isEditingItem'],
      });
    case VIRTUAL_SHOPPING_CART_DELETE_ITEM_SUCCESS:
      if (action.virtualShoppingCart) setVirtualCart(action.virtualShoppingCart);
      return update(state, {
        virtualShoppingCart: { $set: action.virtualShoppingCart },
        $unset: ['isDeletingItem'],
      });
    case VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST:
      return update(state, {
        isPostingToCart: { $set: true },
        $unset: ['postToCartError'],
      });
    case VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        $unset: ['isPostingToCart'],
      });
    case VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_ERROR:
      return update(state, {
        postToCartError: { $set: action.error },
        $unset: ['isPostingToCart'],
      });

    case SHOPPING_CART_FETCH_CART_REQUEST:
      return update(state, {
        isFetchingCart: { $set: true },
        $unset: ['fetchCartError'],
      });
    case SHOPPING_CART_FETCH_CART_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        $unset: ['isFetchingCart'],
      });
    case SHOPPING_CART_FETCH_CART_ERROR:
      return update(state, {
        fetchCartError: { $set: action.error },
        $unset: ['isFetchingCart'],
      });

    case SHOPPING_CART_DELETE_CART_ITEMS_REQUEST:
      return update(state, {
        isDeletingCart: { $set: true },
        $unset: ['deleteCartError'],
      });
    case SHOPPING_CART_DELETE_CART_ITEMS_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        $unset: ['isDeletingCart'],
      });
    case SHOPPING_CART_DELETE_CART_ITEMS_ERROR:
      return update(state, {
        deleteCartError: { $set: action.error },
        $unset: ['isDeletingCart'],
      });
      
    case SHOPPING_CART_DELETE_CART_REQUEST:
      return update(state, {
        isDeletingCart: { $set: true },
        $unset: ['deleteCartError'],
      });
    case SHOPPING_CART_DELETE_CART_SUCCESS:
      return update(state, {
        shoppingCart: { $set: [] },
        $unset: ['isDeletingCart'],
      });
    case SHOPPING_CART_DELETE_CART_ERROR:
      return update(state, {
        deleteCartError: { $set: action.error },
        $unset: ['isDeletingCart'],
      });
    case SHOPPING_CART_POST_ITEM_REQUEST:
    case VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST:
      return update(state, {
        isPostingCartItem: { $set: true },
        $unset: ['postCartItemError', 'postCartItemSuccess'],
      });
    case SHOPPING_CART_POST_ITEM_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        postCartItemSuccess: { $set: true },
        $unset: ['isPostingCartItem'],
      });
    case SHOPPING_CART_POST_ITEM_ERROR:
    case VIRTUAL_SHOPPING_CART_POST_ITEM_ERROR:
      return update(state, {
        postCartItemError: { $set: action.error },
        $unset: ['isPostingCartItem'],
      });
    case SHOPPING_CART_EDIT_ITEM_REQUEST:
    case VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST:
      return update(state, {
        isEditingItem: { $set: true },
        $unset: ['editItemError'],
      });
    case SHOPPING_CART_EDIT_ITEM_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        $unset: ['isEditingItem'],
      });
    case SHOPPING_CART_EDIT_ITEM_ERROR:
    case VIRTUAL_SHOPPING_CART_EDIT_ITEM_ERROR:
      return update(state, {
        editItemError: { $set: action.error },
        $unset: ['isEditingItem'],
      });
    case SHOPPING_CART_DELETE_ITEM_REQUEST:
    case VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST:
      return update(state, {
        isDeletingItem: { $set: true },
        $unset: ['deleteItemError'],
      });
    case SHOPPING_CART_DELETE_ITEM_SUCCESS:
      return update(state, {
        shoppingCart: { $set: action.shoppingCart },
        $unset: ['isDeletingItem'],
      });
    case SHOPPING_CART_DELETE_ITEM_ERROR:
    case VIRTUAL_SHOPPING_CART_DELETE_ITEM_ERROR:
      return update(state, {
        deleteItemError: { $set: action.error },
        $unset: ['isDeletingItem'],
      });
    case SHOPPING_CART_FORECAST_ITEM_REQUEST:
      const shoppingCart = state.shoppingCart;
      if (shoppingCart) {
        const foundIndex = shoppingCart.findIndex((item) => item.number === action.itemNumber);
        if (foundIndex !== -1) {
          const newShoppingCart = [...shoppingCart];
          newShoppingCart[foundIndex].isLoadingForecast = true;
          return {
            ...state,
            shoppingCart: newShoppingCart,
          };
        }
      }
      return state;
    case SHOPPING_CART_FORECAST_ITEM_SUCCESS:
      const shoppingCartS = state.shoppingCart;
      if (shoppingCartS) {
        const foundIndex = shoppingCartS.findIndex((item) => item.number === action.itemNumber);
        if (foundIndex !== -1) {
          const newShoppingCart = [...shoppingCartS];
          newShoppingCart[foundIndex].isLoadingForecast = false;
          if (action.forecastClassic) {
            newShoppingCart[foundIndex].forecastClassic = action.forecastClassic
              .find((f) => f.tier.name === newShoppingCart[foundIndex].serviceTier);
          } else if (action.forecastCouriers) {
            const t = action.forecastCouriers
              .find((f) => (
                newShoppingCart[foundIndex].courier && f.courier.id === newShoppingCart[foundIndex].courier.id
              ));
            newShoppingCart[foundIndex].forecastCourier = t;
          }
          return {
            ...state,
            shoppingCart: newShoppingCart,
          };
        }
      }
      return state;
    case SHOPPING_CART_FORECAST_ITEM_ERROR:
      /*const shoppingCartE = state.shoppingCart;
      if (shoppingCartE) {
        const foundIndex = shoppingCartE.findIndex((item) => item.number === action.itemNumber);
        if (foundIndex !== -1) {
          const newShoppingCart = [...shoppingCartE];
          newShoppingCart[foundIndex].isLoadingForecast = false;
          return {
            ...state,
            shoppingCart: newShoppingCart,
          };
        }
      }*/
      return state;
    default:
      return state;
  }
}

// Epics
export function fetchShoppingCartEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_FETCH_CART_REQUEST),
    mergeMap(() => getShoppingCartApi().pipe(
      map((shoppingCart: ShoppingCartItem[]) => fetchShoppingCartSuccess(shoppingCart)),
      catchError((e: HTTPError) => of(fetchShoppingCartErrorFn(e))),
    )),
  );
}

export function deleteShoppingCartItemsEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_DELETE_CART_ITEMS_REQUEST),
    mergeMap(({ shoppingCartItemIds }) => deleteShoppingCartItemsApi({
      shoppingCartItemIds
    }).pipe(
      map((shoppingCart: ShoppingCartItem[]) => deleteShoppingCartItemsSuccess(shoppingCart)),
      catchError((e: HTTPError) => of(deleteShoppingCartItemsErrorFn(e))),
    )),
  );
}


export function deleteShoppingCartEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_DELETE_CART_REQUEST),
    mergeMap(() => deleteShoppingCartApi().pipe(
      map(() => deleteShoppingCartSuccess()),
      catchError((e: HTTPError) => of(deleteShoppingCartErrorFn(e))),
    )),
  );
}

export function postShoppingCartItemImageEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_POST_ITEM_REQUEST),
    mergeMap(({ shoppingCartRequest }) => {
      const { image, imageURI } = shoppingCartRequest.shoppingCartItems[0];
      let obs = of({});
      if (image) {
        obs = uploadImageURLApi({
          url: image,
          resourceGroup: 'PRODUCT',
        });
      } else if (imageURI) {
        const fd = new FormData();
        fd.append('file', dataURItoBlob(imageURI));
        fd.append('resourceGroup', 'PRODUCT');
        obs = uploadImageApi(fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      return obs.pipe(
        mergeMap((imageResponse: { id: string }) =>
          of(postShoppingCartItemImageSuccess(imageResponse.id, shoppingCartRequest))
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postShoppingCartItemErrorFn(e));
        }));
    }),
  );
}

export function postShoppingCartItemEpic(action$: Observable<ShoppingCartAction>,
                                         state$: StateObservable<RootState>) {
  return action$.pipe(
    ofType(SHOPPING_CART_POST_ITEM_IMAGE_SUCCESS),
    mergeMap(({ imageId, shoppingCartRequest }) => {
      const req = {
        ...shoppingCartRequest,
      };
      if (shoppingCartRequest.shoppingCartItems[0]) {
        req.shoppingCartItems[0].imageId = imageId;
        delete req.shoppingCartItems[0].image;
        delete req.shoppingCartItems[0].imageURI;
      }
      return postShoppingCartItemApi(req).pipe(
        map((shoppingCart: ShoppingCartItem[]) => {
          const productTrackingInfo =
            analytics.mapProduct(shoppingCart[0], state$.value.localValues.categoriesMap, 'item_category');
          analytics.fireCustomEvent('Product Added', {
            category: 'Product Item',
            action: 'Added',
            label: 'CART',
            user_type: 'purchaser',
            user_referrer: window.localStorage.getItem('userReferrer') || '',
            landing_url: window.localStorage.getItem('landingUrl') || '',
            domain_country: state$.value.locale.domainCountry,
            cart_type: 'CART',
            origin_type: req.originType,
            ...productTrackingInfo,
          }, {
            integrations: {
              'All': false,
              'Google Analytics': false,
              'Amplitude': false,
              'Facebook Pixel': true,
            },
          });
          return postShoppingCartItemSuccess(shoppingCart);
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            let message = 'httpErrors.post.400';
            if (typeof e.data === 'object' && e.data['name'] === 'WeightExceededError') {
              message = 'newOrder.product.cart.maxWeightExceeded';
            }
            return of(postShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, message),
            );
          }
          return of(postShoppingCartItemErrorFn(e));
        }));
    }),
  );
}

export function editShoppingCartItemEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_EDIT_ITEM_REQUEST),
    mergeMap(({ amount, shipping, itemNumber }) => {
      const r: { [key: string]: number | string } = {};
      if (typeof amount === 'number') {
        r.amount = amount;
      }
      if (!!shipping) {
        r.shipping = shipping;
      }
      return putShoppingCartItemApi(r, {
        route: endpoints.PUT_SHOPPING_CART_ITEM.replace(':itemNumber', itemNumber),
      }).pipe(
        map((shoppingCart: ShoppingCartItem[]) => editShoppingCartItemSuccess(shoppingCart)),
        catchError((e: HTTPError) => {
          if (e.code === 404) {
            return of(editShoppingCartItemErrorFn(e), fetchShoppingCart());
          }
          return of(editShoppingCartItemErrorFn(e));
        }),
      );
    }),
  );
}

export function deleteItemEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(SHOPPING_CART_DELETE_ITEM_REQUEST),
    mergeMap(({ itemNumber }) => deleteShoppingCartItemApi(undefined, {
      route: endpoints.DELETE_SHOPPING_CART_ITEM.replace(':itemNumber', itemNumber),
    }).pipe(
      map((shoppingCart: ShoppingCartItem[]) => deleteItemSuccess(shoppingCart)),
      catchError((e: HTTPError) => {
        if (e.code === 404) {
          return of(deleteItemErrorFn(e), fetchShoppingCart());
        }
        return of(deleteItemErrorFn(e));
      }),
    )),
  );
}

export function postVirtualShoppingCartItemEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_POST_ITEM_REQUEST),
    mergeMap(({ virtualShoppingCartRequest }) => postItemsAutoShippingApi(virtualShoppingCartRequest.shippingRequest).pipe(
      map((shippingResponses: ShippingResponse[]) => {
        // There might not be a virtualCart initialized
        const virtualCart = getVirtualCart() ? [...getVirtualCart()] : [];
        shippingResponses.forEach((shippingResponse: ShippingResponse) => {
          const foundIndex = virtualCart.findIndex((virtualItem: VirtualItem) =>
            virtualItem.itemId === shippingResponse.itemId);
          if (foundIndex !== -1) {
            virtualCart[foundIndex].shipping = shippingResponse.shipping;
          } else {
            virtualCart.push({
              ...virtualShoppingCartRequest.item,
              shipping: shippingResponse.shipping,
              onlineStore: shippingResponse.onlineStore
            });
          }
        });
        return postVirtualShoppingCartItemSuccess(virtualCart)
      }),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(postVirtualShoppingCartItemErrorFn(e),
            setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(postVirtualShoppingCartItemErrorFn(e),
            setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(postVirtualShoppingCartItemErrorFn(e));
      }),
    )),
  );
}

export function editVirtualShoppingCartItemEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_EDIT_ITEM_REQUEST),
    mergeMap(({ index, amount, shipping }) => {
      // There is a virtualCart, you are editing an item
      const virtualCart = getVirtualCart();
      const items = virtualCart.map((virtualItem: VirtualItem, i: number) => {
        const shippingItemRequest: { [key: string]: any } = {
          itemId: virtualItem.itemId,
          price: virtualItem.price,
          url: virtualItem.url,
        };
        if (typeof amount === 'number' && !!amount && index === i) {
          shippingItemRequest.amount = amount;
          virtualCart[i].amount = amount;
        } else {
          shippingItemRequest.amount = virtualItem.amount;
        }
        if (typeof shipping === 'string' && !!shipping && index === i) {
          shippingItemRequest.shipping = shipping;
          shippingItemRequest.isShippingAutomatic = false;
          virtualCart[i].shipping = shipping;
          virtualCart[i].isShippingAutomatic = false;
        } else if (typeof virtualItem.isShippingAutomatic !== 'undefined') {
          shippingItemRequest.shipping = virtualItem.shipping;
          shippingItemRequest.isShippingAutomatic = virtualItem.isShippingAutomatic;
        }
        return shippingItemRequest;
      });
      const shippingRequest: ShippingRequestApi = {
        purchaseCountry: /*departureCountry.value*/'US',
        items,
      };
      return postItemsAutoShippingApi(shippingRequest).pipe(
        map((shippingResponses: ShippingResponse[]) => {
          shippingResponses.forEach((shippingResponse: ShippingResponse) => {
            const foundIndex = virtualCart.findIndex((virtualItem: VirtualItem) =>
              virtualItem.itemId === shippingResponse.itemId);
            // It should always found one
            if (foundIndex !== -1) {
              virtualCart[foundIndex].shipping = shippingResponse.shipping;
            }
          });
          return editVirtualShoppingCartItemSuccess(virtualCart)
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editVirtualShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(editVirtualShoppingCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(editVirtualShoppingCartItemErrorFn(e));
        }),
      );
    }),
  );
}

export function deleteVirtualCartItemEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_DELETE_ITEM_REQUEST),
    mergeMap(({ index }) => {
      // There is a virtualCart, you are deleting an item
      const virtualCart = update(getVirtualCart(), {
        $splice: [[index, 1]],
      });
      const items = virtualCart.map((virtualItem: VirtualItem, i: number) => {
        const shippingItemRequest: { [key: string]: any } = {
          amount: virtualItem.amount,
          itemId: virtualItem.itemId,
          price: virtualItem.price,
          url: virtualItem.url,
        };
        if (typeof virtualItem.isShippingAutomatic !== 'undefined') {
          shippingItemRequest.shipping = virtualItem.shipping;
          shippingItemRequest.isShippingAutomatic = virtualItem.isShippingAutomatic;
        }
        return shippingItemRequest;
      });
      const shippingRequest: ShippingRequestApi = {
        purchaseCountry: /*departureCountry.value*/'US',
        items,
      };
      return postItemsAutoShippingApi(shippingRequest).pipe(
        map((shippingResponses: ShippingResponse[]) => {
          shippingResponses.forEach((shippingResponse: ShippingResponse) => {
            const foundIndex = virtualCart.findIndex((virtualItem: VirtualItem) =>
              virtualItem.itemId === shippingResponse.itemId);
            // It should always found one
            if (foundIndex !== -1) {
              virtualCart[foundIndex].shipping = shippingResponse.shipping;
            }
          });
          return deleteVirtualCartItemSuccess(virtualCart);
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(deleteVirtualCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(deleteVirtualCartItemErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(deleteVirtualCartItemErrorFn(e));
        }),
      );
    }),
  );
}

export function postVirtualCartToCartDeleteEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_REQUEST),
    mergeMap(({ shoppingCartRequest }) => deleteShoppingCartApi().pipe(
      map(() => postVirtualCartToCartDeleteSuccess(shoppingCartRequest)),
      catchError((e: HTTPError) => of(deleteShoppingCartErrorFn(e))),
    )),
  );
}

export function postVirtualCartToCartImageEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_DELETE_SUCCESS),
    mergeMap(({ shoppingCartRequest }) => {
      const obs = shoppingCartRequest.shoppingCartItems.map((item: ShoppingCartItemRequestApi) => {
        if (item.image) {
          return uploadImageURLApi({
            url: item.image,
            resourceGroup: 'PRODUCT',
          });
        } else if (item.imageURI) {
          const fd = new FormData();
          fd.append('file', dataURItoBlob(item.imageURI));
          fd.append('resourceGroup', 'PRODUCT');
          return uploadImageApi(fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      });
      return forkJoin(obs).pipe(
        mergeMap((imagesResponse: { id: string }[]) =>
          of(postVirtualCartToCartImageSuccess(imagesResponse.map(({ id }) => id), shoppingCartRequest))
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postVirtualCartToCartErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postVirtualCartToCartErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postVirtualCartToCartErrorFn(e));
        }),
      );
    }),
  );
}

export function postVirtualCartToCartEpic(action$: Observable<ShoppingCartAction>) {
  return action$.pipe(
    ofType(VIRTUAL_SHOPPING_CART_POST_VIRTUAL_CART_TO_CART_IMAGE_SUCCESS),
    mergeMap(({ imageIds, shoppingCartRequest }) => {
      imageIds.forEach((imageId: string, n: number) => {
        shoppingCartRequest.shoppingCartItems[n].imageId = imageId;
        delete shoppingCartRequest.shoppingCartItems[n].image;
        delete shoppingCartRequest.shoppingCartItems[n].imageURI;
      });
      return postShoppingCartItemApi(shoppingCartRequest).pipe(
        mergeMap((shoppingCart: ShoppingCartItem[]) =>
          // deleteVirtualShoppingCart() should be called last because it triggers an update.
          // This update at "QuoteStep" triggers an undesired redirect only to be redirected again by this`push()`
          of(postVirtualCartToCartSuccess(shoppingCart), push(`/new-order/checkout/cart`), deleteVirtualShoppingCart())),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postVirtualCartToCartErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postVirtualCartToCartErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.400'),
            );
          } else if (e.code === 401) {
            return of(postVirtualCartToCartErrorFn(e),
              setNewAlert('shopping-cart', 'error', undefined, 'httpErrors.post.401'),
            );
          }
          return of(postVirtualCartToCartErrorFn(e));
        }),
      );
    }),
  );
}

export function forecastShoppingCartItemEpic(action$: Observable<ShoppingCartAction>, state$: StateObservable<RootState>) {
  return action$.pipe(
    ofType(SHOPPING_CART_FORECAST_ITEM_REQUEST),
    mergeMap(({ itemNumber }) => {
      const item = state$.value.shoppingCart.shoppingCart.find((item) => item.number === itemNumber);
      
      const itemReq: {[key: string]: any} = {
        url: item.url,
        name: item.name,
        categoryId: parseInt(item.itemCategoryId, 10),
        price: item.price,
        amount: item.amount,
        originalPackaging: item.originalPackaging,
        shipping: item.shipping,
      };

      if (item.volumeTier) itemReq.volumeTier = item.volumeTier.code;
      if (item.weight) itemReq.weight = item.weight;
      
      const req = {
        deliveryCountry: 'PE',
        purchaseCountry: 'US',
        items: [itemReq]
      };
      
      let obs: Observable;
      
      if (item.courier) {
        obs = postForecastDiyPriceApi(req).pipe(
          mergeMap((forecastCouriers: ForecastCourier[]) => {
            return of(forecastShoppingCartItemSuccess(itemNumber, undefined, forecastCouriers));
          }),
          catchError((e: HTTPError) => of(forecastShoppingCartItemError(itemNumber))),
        );
      } else {
        obs = postForecastClassicPriceApi(req).pipe(
          mergeMap((forecastClassic: ForecastTier[]) => {
            return of(forecastShoppingCartItemSuccess(itemNumber, forecastClassic));
          }),
          catchError((e: HTTPError) => of(forecastShoppingCartItemError(itemNumber))),
        );
      }
      return obs;
    }),
  );
}



// WEBPACK FOOTER //
// ./src/modules/shopping-cart/duck.js